import React, { useContext, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import CriterionC from './components/criterionC/criterionC'
import EcosystemData from './components/ecosystemData/ecoSystemList'
import { SiteContext } from 'providers/site'
import SpeciesDetails from './components/speciesDetails'
import CriterionE from './components/criterionE/criterionE'
import ScrollToTop from 'components/ScrollToTop'
import CongregationsList from './components/congregationsSection/congregationsList'
import { ProfileContext } from 'providers/profile'
import { canViewSensitive } from 'utils/rolePermissions'
import { useIBAAccess } from 'hooks/useIBAAccess'
import { SITE_STATUS_NOMINATED_ID, SITE_STATUS_PROPOSED_ID, SITE_STATUS_REVIEWED_BY_RFP_ID } from 'constants/siteStatuses'
import KbaCriteriaSummary from 'flow/siteProposal/pages/calculations/components/KbaCriteriaSummary'
import { useOverallKbaCriteria } from './hooks/useOverallKbaCriteria'
import { resolveSiteWorkflowContext } from 'utils/resolveSiteWorkflowIds'
import { OverallKbaCriteriaRefreshContext } from './context/OverallKbaCriteriaRefreshContext'
import { resolveCatalogCriteriaFallback } from 'flow/siteProposal/pages/calculations/utils/computeOverallKbaCriteria'

const SiteProposalAssessmentPage = () => {
  const params = useParams()
  const { profile } = useContext(ProfileContext)
  const { site, setSiteProposalFieldValidation } = useContext(SiteContext)

  const { canSeeSiteIBACongregations } = useIBAAccess()

  const { siteId, historyId, editOnly } = useMemo(() => {
    return resolveSiteWorkflowContext(site, params)
  }, [site, params])

  const isActiveReassessment = Boolean(params.siteHistoryId) && !editOnly
  const shouldRefreshCriteriaLive = isActiveReassessment
  // View on a live site has no history in the URL; read criteria from the latest live assessment.
  const criteriaHistoryId = historyId ?? site?.latestLiveHistoryId
  const excludeDisqualifiedTriggers = isActiveReassessment || (!editOnly && Boolean(criteriaHistoryId))
  const { criteria, isLoading, refetch } = useOverallKbaCriteria(
    siteId,
    criteriaHistoryId,
    excludeDisqualifiedTriggers
  )

  const displayedCriteria = useMemo(() => {
    if (criteria.length > 0) {
      return criteria
    }

    if (!excludeDisqualifiedTriggers) {
      return resolveCatalogCriteriaFallback(site)
    }

    return criteria
  }, [criteria, excludeDisqualifiedTriggers, site])

  const showCriteriaSummary = !isLoading && displayedCriteria.length > 0

  const showCongregations = canSeeSiteIBACongregations(site) || (site.SiteStatusId === SITE_STATUS_PROPOSED_ID || site.SiteStatusId === SITE_STATUS_NOMINATED_ID || site.SiteStatusId === SITE_STATUS_REVIEWED_BY_RFP_ID || canViewSensitive(profile.RoleId))

  if (!site.id) {
    return null
  }

  return (
    <OverallKbaCriteriaRefreshContext.Provider
      value={{ refetchCriteria: shouldRefreshCriteriaLive ? refetch : () => {} }}
    >
      <div>
        <KbaCriteriaSummary criteria={displayedCriteria} isLoading={isLoading} />
        <SpeciesDetails site={site} hasCriteriaSummary={showCriteriaSummary} />
        {showCongregations && (
          <CongregationsList
            site={site}
            setSiteProposalFieldValidation={setSiteProposalFieldValidation}
            showCongregations={showCongregations}
          />
        )}
        <EcosystemData site={site} />
        <CriterionC site={site} />
        <CriterionE site={site} />
        <ScrollToTop bottom='104px' />
      </div>
    </OverallKbaCriteriaRefreshContext.Provider>
  )
}

export default SiteProposalAssessmentPage
